import React, { useState, useContext, useRef } from "react";
import { GlobalContext } from "../../../App"; // Adjust path as needed
import L from "leaflet";
import { HOST } from "../../host";
import files from "../../static";


export default function RasterToVector() {
  const {
    map,
    tools,
    vis,
    setCoder,
    layerControls,
    selectedLayers,
    Canvas,
    setTools,
    getCsrfToken,
  } = useContext(GlobalContext);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vectorLayer, setVectorLayer] = useState(null);
  const [conversionType, setConversionType] = useState("point");
  const fileInputRef = useRef(null);
  const [show, setshow] = useState(false);
  const toolRef = useRef(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError(null);
};

const handleSubmit = async () => {
  if (!file) {
    setError("Please select a raster file first");
    return;
  }

  setLoading(true);
  setError(null);

  try {
    if (vectorLayer && map) {
      map.removeLayer(vectorLayer);
      setVectorLayer(null);
    }

    const formData = new FormData();
    formData.append("raster", file);
    formData.append("conversion_type", conversionType);

   const response = await fetch(`${HOST}/raster-to-vector-view`, {
        method: "POST",
        credentials: "include",
        body: formData,
        headers: {
          Accept: "application/json",
          "X-CSRFToken": await getCsrfToken(),
        },
      });

    if (!response.ok) {
      const raw = await response.text();
      let detail = `HTTP ${response.status}`;
      try {
        const parsed = JSON.parse(raw);
        detail = parsed.error || parsed.detail || detail;
      } catch {
        console.error("Server error (check Django terminal):", raw);
        detail = `Server error ${response.status} — see browser console for traceback`;
      }
      throw new Error(detail);
    }

    const geojson = await response.json();
    console.log("Received GeoJSON:", geojson);

    if (!geojson?.features || !Array.isArray(geojson.features)) {
      throw new Error("Invalid GeoJSON format received from server");
    }

    const newVectorLayer = L.geoJSON(geojson, {
      pointToLayer: (feature, latlng) => {
        if (conversionType === "point") {
          return L.circleMarker(latlng, {
            radius: 4,
            fillColor: getColorForValue(feature?.properties?.value),
            color: "#ffffff",
            weight: 1,
            opacity: 1,
            fillOpacity: 0.8,
          });
        }
        return L.marker(latlng);
      },
      style: (feature) => ({
        fillColor: getColorForValue(feature?.properties?.value),
        weight: 1,
        opacity: 1,
        color: "#2f2f2f",
        fillOpacity: 0.7,
      }),
      onEachFeature: (feature, layer) => {
        const value = feature?.properties?.value;
        const valueText =
          typeof value === "number" ? value.toFixed(2) : value;
        layer.bindPopup(`Value: ${valueText ?? "N/A"}`);
      },
    }).addTo(map);

    map.fitBounds(newVectorLayer.getBounds());
    setVectorLayer(newVectorLayer);

    if (layerControls) {
      layerControls.addOverlay(
        newVectorLayer,
        conversionType === "point" ? "Raster to Points" : "Raster to Polygons"
      );
    }
  } catch (err) {
    console.error("Conversion error:", err);
    setError(err.message);
  } finally {
    setLoading(false);
  }
};


const getColorForValue = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '#bdbdbd';
    }
    // Custom color scale based on your data range
    const normalized = (Number(value) - 0) / (100 - 0); // Adjust min/max as needed
    if (normalized < 0.2) return '#ffffcc';
    if (normalized < 0.4) return '#a1dab4';
    if (normalized < 0.6) return '#41b6c4';
    if (normalized < 0.8) return '#2c7fb8';
    return '#253494';
};

  const resetForm = () => {
    setFile(null);
    setError(null);
    setConversionType("point");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

return (
  <div style={{ position: vis ? "absolute" : "relative" }} className="toolscont">
    <div>
          <button
        className="btn text-white"
        title="Raster to Vector Conversion"
        onClick={() => setshow(!show)}
        style={{
          backgroundColor: "black",
          width: "40px", height: "40px",
          borderRadius: "50%",
        }}
      >
        <i className="fa-solid fa-layer-group" style={{ fontSize: "16px" }} />
      </button>
    </div>

    {show && (
      <div
        ref={toolRef}
        className="toolsvis"
        id="toolvis"
        style={{ display: "flex", flexDirection: "column", width: "260px", padding: "10px 12px" }}
      >
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          paddingBottom: "8px", marginBottom: "10px",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}>
         <span style={{ color: "#ccc", fontSize: "13px", fontWeight: 600 }}>
          <i
            className="fa-solid fa-layer-group"
            style={{ marginRight: "6px", fontSize: "11px" }}
          />
          Raster to vector
        </span>
          <span
            onClick={() => setshow(false)}
            style={{ color: "#e74c3c", fontSize: "20px", cursor: "pointer", lineHeight: 1 }}
          >
            &times;
          </span>
        </div>

        {/* Inputs */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>

          {/* Output type */}
          <div>
            <label style={{ fontSize: "11px", color: "#aaa", marginBottom: "3px", display: "block" }}>
              Output type
            </label>
            <div style={{ display: "flex", gap: "16px" }}>
              {["point", "vector"].map((val) => (
                <label key={val} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: "#ccc", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="conversionType"
                    value={val}
                    checked={conversionType === val}
                    onChange={(e) => setConversionType(e.target.value)}
                    disabled={loading}
                    style={{ accentColor: "#3b82f6" }}
                  />
                  {val === "point" ? "Point" : "Vector (polygon)"}
                </label>
              ))}
            </div>
          </div>

          {/* File input */}
          <div>
            <label style={{ fontSize: "11px", color: "#aaa", marginBottom: "3px", display: "block" }}>
              GeoTIFF file
            </label>
            <input
              type="file"
              ref={fileInputRef}
              accept=".tif,.tiff"
              onChange={handleFileChange}
              disabled={loading}
              style={{
                width: "100%",
                fontSize: "12px",
                height: "30px",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.25)",
                color: "#aaa",
                borderRadius: "4px",
                padding: "3px 6px",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Error */}
          {error && (
            <div style={{
              fontSize: "11px", color: "#f87171",
              background: "rgba(248,113,113,0.1)",
              border: "1px solid rgba(248,113,113,0.25)",
              borderRadius: "4px", padding: "5px 8px",
            }}>
              {error}
            </div>
          )}

          {/* Convert button */}
          <button
            onClick={handleSubmit}
            disabled={!file || loading}
            className="btn btn-primary border-0"
            style={{ width: "100%", height: "32px", fontSize: "12px", marginTop: "2px" }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Converting…
              </>
            ) : (
              `Convert to ${conversionType === "point" ? "point" : "polygon"}`
            )}
          </button>

          {/* Success */}
          {vectorLayer && (
            <div style={{
              display: "flex", alignItems: "center", gap: "6px",
              fontSize: "11px", color: "#4ade80",
              background: "rgba(74,222,128,0.1)",
              border: "1px solid rgba(74,222,128,0.25)",
              borderRadius: "4px", padding: "5px 8px",
            }}>
              <i className="fa-solid fa-circle-check" style={{ fontSize: "13px" }} />
              Vector layer added to map
            </div>
          )}

        </div>
      </div>
    )}
  </div>
);
}

// 2414.tif